import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { queryClient } from './lib/queryClient'
import { resumePendingSseReconnectTelemetry } from './lib/sseReconnectTelemetry'
import App from './App'
import './index.css'

if (import.meta.env.VITE_SENTRY_DSN) {
  const getFloat = (val: string | undefined, fallback: number) => {
    if (!val) return fallback
    const parsed = parseFloat(val)
    return isNaN(parsed) ? fallback : parsed
  }

  const getTargets = (val: string | undefined) => {
    if (!val) return ['localhost', /^https:\/\/yourserver\.io\/api/]
    return val.split(',').map(t => {
      const trimmed = t.trim()
      return trimmed.startsWith('^') ? new RegExp(trimmed) : trimmed
    })
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_VERSION || 'release-development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // PII
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII !== 'false',
    // Tracing
    tracesSampleRate: getFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      1.0
    ),
    tracePropagationTargets: getTargets(
      import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS
    ),
    // Session Replay
    replaysSessionSampleRate: getFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0.1
    ),
    replaysOnErrorSampleRate: getFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0
    ),
  })

  resumePendingSseReconnectTelemetry()
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
)
